import { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
} from "recharts";
import styles from "./statistic.module.css";
import StatisticFormat from "components/statisticFormat/statisticFormat";
import { statistic9 } from "services/statistic/statistic9";
import { generateCSVContent, downloadFile } from "utils/statistic";
import { Statistik9Data } from "modules/statistic";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default function Statistic9() {
  const [data, setData] = useState<Statistik9Data[]>([]);

  const fetchData = async () => {
    try {
      const res = await statistic9();
      const { data } = res;
      setData(data);
    } catch (error) {
      console.error("Fehler beim Laden der Daten:", error);
    }
  };

  // CSV download including auslastung
  const downloadCSV = () => {
    const headers = ["Year", "Total Bookings", "Auslastung (%)"];

    const rows: (string | number)[][] = data.map((entry) => [
      entry.year,
      entry.total_bookings,
      entry.auslastung ?? "-",
    ]);

    const csvContent = generateCSVContent(headers, rows);
    downloadFile(csvContent, "Anlage5.csv");
  };

  // PDF download including auslastung
  const downloadPDF = () => {
    if (!data || data.length === 0) return;

    const doc = new jsPDF("p", "pt", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();

    // Title
    doc.setFontSize(18);
    doc.text("Gesamtanzahl der Buchungen pro Jahr", pageWidth / 2, 30, { align: "center" });

    // Table
    const headers = [["Jahr", "Gesamtbuchungen", "Auslastung (%)"]];
    const rows = data.map((entry) => [
      entry.year,
      entry.total_bookings,
      entry.auslastung ?? "-",
    ]);

    autoTable(doc, {
      startY: 50,
      head: headers,
      body: rows,
      theme: "grid",
      headStyles: { fillColor: [200, 200, 200], textColor: [0, 0, 0], fontStyle: "bold" },
      bodyStyles: { halign: "center", textColor: [51, 51, 51] },
      styles: { fontSize: 10, cellPadding: 3 },
    });

    doc.save("Anlage5.pdf");
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <StatisticFormat
      label=""
      description=""
      onCsvButton1Click={downloadCSV}
      onPdfButton1Click={downloadPDF}
    >
      <div className={styles.container}>
        <ResponsiveContainer width="100%" height={450} maxHeight={450}>
          <BarChart
            data={data}
            margin={{ top: 40, right: 50, left: 20, bottom: 5 }}
            style={{ maxHeight: "420px" }}
          >
            <text
              x="50%"
              y={20}
              textAnchor="middle"
              dominantBaseline="middle"
              style={{ fontSize: "16px", fontWeight: "bold" }}
            >
              Gesamtanzahl der Buchungen pro Jahr
            </text>

            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="year" />

            {/* المحور الأيسر للحجوزات */}
            <YAxis
              yAxisId="left"
              label={{ value: "Buchungen", angle: -90, position: "insideLeft" }}
            />

            {/* المحور الأيمن للأشغال */}
            <YAxis
              yAxisId="right"
              orientation="right"
              label={{ value: "Auslastung (%)", angle: 90, position: "insideRight" }}
              domain={[0, 100]} // لأن النسبة لا تتجاوز 100%
            />

            <Tooltip
              formatter={(value: number, name: string) => {
                return name === "Auslastung (%)" ? `${value}%` : value;
              }}
            />
            <Legend />

            <Bar yAxisId="left"
              dataKey="total_bookings"
              fill="#0170c1"
              name="Buchungen"
              isAnimationActive={true}
              animationDuration={1000}
              animationEasing="ease-out" />
            <Bar yAxisId="right"
              dataKey="auslastung"
              fill="#f39c12"
              name="Auslastung (%)"
              isAnimationActive={true}
              animationDuration={1200}
              animationEasing="ease-out" />
          </BarChart>
        </ResponsiveContainer>

      </div>
    </StatisticFormat>
  );
}
